import { useState, useEffect } from "react";
import {
  SandpackProvider,
  SandpackThemeProvider,
} from "@codesandbox/sandpack-react";

import useSound from "use-sound";
import { useHotkeys } from "react-hotkeys-hook";

import Preview from "./components/Preview";
import CodeEditor from "./components/CodeEditor";
import SelectTheme from "./components/SelectTheme";
import FileTree from "./components/FileTree";

import "@codesandbox/sandpack-react/dist/index.css";

import fantasy16bit from "./sounds/fantasy16bit/Sounds";
import calm from "./sounds/calm/Sounds";

let soundExperience = {
  fantasy16bit,
  calm,
};

const options = [
  {
    key: "fantasy16bit",
    label: "Fantasy Theme",

    theme: "monoindustrial",
    bg: "#222C28",
  },
  { key: "calm", label: "Calm Theme", theme: "blackboard", bg: "#0C1021" },
];

const DEFAULT_KEY = "calm";

function App() {
  const [themeKey, setThemeKey] = useState(DEFAULT_KEY);
  const [theme, setTheme] = useState(soundExperience[DEFAULT_KEY]);
  const [paused, setPaused] = useState(false);
  const [monacoTheme, setMonaco] = useState(
    options.find((e) => e.key === DEFAULT_KEY).theme
  );
  const localStorageFiles = localStorage.getItem("files");
  const customSetup = {
    entry: "/index.js",
    files: {
      "/App.js": `export default function App() {\n\treturn (\n\t\t<div>\n\t\t\t<h1>Hello World</h1>\n\t\t</div>\n\t)\n}`,
    },
  };
  if (localStorageFiles) {
    customSetup.files = JSON.parse(localStorageFiles);
  }

  const [playAmbient, { stop: stopAmbient }] = useSound(theme.ambientTrack[0], {
    volume: 0.2,
  });

  const changeTheme = (key) => {
    setThemeKey(key);
    setTheme(soundExperience[key]);
    setMonaco(options.find((e) => e.key === key).theme);
    setPaused(false);
  };

  useEffect(() => {
    stopAmbient();
    playAmbient();
  });

  useEffect(() => {
    if (paused) {
      stopAmbient();
    } else {
      playAmbient();
    }
  }, [paused]);

  useEffect(() => {
    stopAmbient();
  }, [theme]);

  const changeFile = () => {
    let arr = theme.eventSounds.newFile;
    let sound = new Audio();
    sound.src = arr[Math.floor(Math.random() * arr.length)];
    sound.play();
  };

  const playPreviewSuccess = () => {
    let arr = theme.eventSounds.previewReload;
    let sound = new Audio();
    sound.src = arr[Math.floor(Math.random() * arr.length)];
    sound.play();
  };

  let word = "";
  useHotkeys(
    "*",
    (e) => {
      console.log(e.key);

      if (["Shift", "Control", "Alt", "Meta"].includes(e.key)) return;

      if (
        e.key === "Enter" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowLeft"
      ) {
        let arr = theme.defaultSound;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
        return;
      }

      word += e.key;

      // Function
      if (word.includes("function")) {
        let arr = theme.eventSounds.function;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
        word = "";
      }

      // Assign Arrows
      if (
        word.includes("=>") ||
        word.includes("let") ||
        word.includes("const") ||
        word.includes("=")
      ) {
        let arr = theme.eventSounds.assign;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
        word = "";
      }

      // React
      if (word.includes("useState") || word.includes("useEffect")) {
        let arr = theme.eventSounds.reactState;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
        word = "";
      }

      // Open Tag
      if (word.match(/<[^>]*>/g)) {
        console.log("a");
        let arr = theme.eventSounds.openTag;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
        word = "";
      }

      if (word !== "" && Math.random() > 0.95) {
        let arr = theme.randomSounds;
        let sound = new Audio();
        sound.src = arr[Math.floor(Math.random() * arr.length)];
        sound.play();
      }
    },
    { enableOnTags: "TEXTAREA" },
    [theme]
  );

  return (
    <div
      className="wrap"
      style={{
        backgroundColor: options.find((e) => e.key === themeKey).bg,
        "--bg:": options.find((e) => e.key === themeKey).bg,
      }}
    >
      <main>
        <SandpackProvider template="react" customSetup={customSetup}>
          <div>
            <FileTree changeFile={changeFile} />
          </div>
          <div>
            <CodeEditor
              theme={monacoTheme}
              playPreviewSuccess={playPreviewSuccess}
            />
          </div>

          <div>
            <Preview />
          </div>
        </SandpackProvider>
      </main>

      <div style={{ zIndex: 999 }}>
        <SelectTheme
          options={options}
          activeOption={themeKey}
          changeTheme={changeTheme}
          paused={paused}
          toogleExperience={() => {
            paused ? setPaused(false) : setPaused(true);
          }}
        />
      </div>

      <a
        href="https://twitter.com/zehf/status/1460596997635907592"
        target="_blank"
        rel="noreferrer"
        style={{
          zIndex: 9999,
          textDecoration: "none",
          position: "fixed",
          bottom: 20,
          right: 30,
          fontSize: "0.9rem",
          fontWeight: "bold",
          color: "white",
          display: "block",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        }}
      >
        About
      </a>
    </div>
  );
}

export default App;
